(() => {
  const body = document.body;
  if (!body.classList.contains('experience-v141')) return;

  const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionEnabled = body.dataset.motionEnabled !== 'false' && !reducedMotion;
  const pointerEnabled = body.dataset.pointerEnabled !== 'false' && motionEnabled && window.matchMedia('(pointer: fine)').matches;
  const motionIntensity = Number.parseFloat(getComputedStyle(body).getPropertyValue('--motion-intensity')) || 0.6;
  const storyRailIndex = document.querySelector('[data-story-index]');
  const storyRailLabel = document.querySelector('[data-story-label]');

  let storySections = [];
  let narratives = [];
  let houseStories = [];
  let spectrumConsoles = [];
  let activeStorySection = null;
  let scrollFrame = 0;

  const setNarrativeStep = (section, index) => {
    const state = section._lementsNarrative;
    if (!state || !state.steps.length) return;
    const boundedIndex = clamp(index, 0, state.steps.length - 1);
    if (boundedIndex === state.activeIndex) return;

    state.activeIndex = boundedIndex;
    const activeStep = state.steps[boundedIndex];
    section.dataset.activeStep = String(boundedIndex);
    section.dataset.activePhase = activeStep.dataset.narrativePhase || 'signal';
    state.steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === boundedIndex));
    state.jumpButtons.forEach((button, buttonIndex) => {
      if (buttonIndex === boundedIndex) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    if (state.title) state.title.textContent = activeStep.dataset.stageTitle || '';
    if (state.caption) state.caption.textContent = activeStep.dataset.stageCaption || '';
    if (state.counter) {
      state.counter.textContent = String(boundedIndex + 1).padStart(2, '0') + ' / ' + String(state.steps.length).padStart(2, '0');
    }
  };

  const initNarrative = (section) => {
    if (section._lementsNarrative) return;
    const steps = [...section.querySelectorAll('[data-narrative-step]')];
    if (!steps.length) return;

    const state = {
      steps,
      jumpButtons: [...section.querySelectorAll('[data-narrative-jump]')],
      title: section.querySelector('[data-narrative-stage-title]'),
      caption: section.querySelector('[data-narrative-stage-caption]'),
      counter: section.querySelector('[data-narrative-counter]'),
      activeIndex: -1
    };
    section._lementsNarrative = state;

    state.jumpButtons.forEach((button) => button.addEventListener('click', () => {
      const target = state.steps[Number(button.dataset.narrativeJump)];
      target?.scrollIntoView({ behavior: motionEnabled ? 'smooth' : 'auto', block: 'center' });
    }));
    setNarrativeStep(section, 0);
  };

  const updateNarrative = (section, viewportHeight) => {
    const state = section._lementsNarrative;
    if (!state) return;
    const marker = viewportHeight * (window.innerWidth <= 900 ? 0.72 : 0.52);
    const centers = state.steps.map((step) => {
      const bounds = step.getBoundingClientRect();
      return bounds.top + bounds.height / 2;
    });
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    centers.forEach((center, index) => {
      const distance = Math.abs(center - marker);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    const range = Math.max(centers[centers.length - 1] - centers[0], 1);
    const progress = clamp((marker - centers[0]) / range);
    section.style.setProperty('--narrative-progress', progress.toFixed(4));
    setNarrativeStep(section, closestIndex);
  };

  const activateHouseStep = (section, index) => {
    const state = section._lementsHouse;
    if (!state || !state.steps.length) return;
    const boundedIndex = clamp(index, 0, state.steps.length - 1);
    if (boundedIndex === state.activeIndex) return;

    state.activeIndex = boundedIndex;
    const activeStep = state.steps[boundedIndex];
    const accent = activeStep.dataset.houseAccent || 'var(--life)';
    state.steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === boundedIndex));
    section.dataset.houseActive = String(boundedIndex);
    section.style.setProperty('--house-accent', accent);
    section.dataset.storyAccent = accent;
    if (activeStorySection === section) body.style.setProperty('--story-accent', accent);
  };

  const initHouseStory = (section) => {
    if (section._lementsHouse) return;
    const steps = [...section.querySelectorAll('[data-house-step]')];
    section._lementsHouse = { steps, activeIndex: -1 };
    activateHouseStep(section, 0);
  };

  const updateHouseStory = (section, viewportHeight) => {
    const state = section._lementsHouse;
    if (!state || !state.steps.length) return;
    if (window.innerWidth <= 900) {
      const marker = viewportHeight * 0.68;
      let activeIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      state.steps.forEach((step, index) => {
        const bounds = step.getBoundingClientRect();
        const distance = Math.abs(bounds.top + bounds.height / 2 - marker);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });
      activateHouseStep(section, activeIndex);
      return;
    }
    const bounds = section.getBoundingClientRect();
    const travel = Math.max(bounds.height - viewportHeight, 1);
    const progress = clamp(-bounds.top / travel);
    const activeIndex = Math.round(progress * (state.steps.length - 1));
    activateHouseStep(section, activeIndex);
  };

  const activateSpectrum = (section, index, source = 'scroll') => {
    const state = section._lementsSpectrum;
    if (!state || !state.cards.length) return;
    const boundedIndex = clamp(index, 0, state.cards.length - 1);
    if (boundedIndex === state.activeIndex && source === 'scroll') return;

    state.activeIndex = boundedIndex;
    if (source !== 'scroll') state.manualScrollY = window.scrollY;
    const activeCard = state.cards[boundedIndex];
    state.cards.forEach((card, cardIndex) => {
      const active = cardIndex === boundedIndex;
      card.classList.toggle('is-active', active);
      card.setAttribute('aria-pressed', String(active));
    });
    const color = activeCard.dataset.frequencyColor || 'var(--ion)';
    section.style.setProperty('--frequency-color', color);
    section.dataset.storyAccent = color;
    if (state.liveLabel) state.liveLabel.textContent = activeCard.dataset.frequencyLabel || '';
    if (state.mobileLiveLabel) state.mobileLiveLabel.textContent = activeCard.dataset.frequencyLabel || '';
    if (activeStorySection === section) body.style.setProperty('--story-accent', color);
  };

  const initSpectrum = (section) => {
    if (section._lementsSpectrum) return;
    const state = {
      cards: [...section.querySelectorAll('[data-spectrum-card]')],
      grid: section.querySelector('.spectrum-grid'),
      liveLabel: section.querySelector('[data-spectrum-live]'),
      mobileLiveLabel: section.querySelector('[data-spectrum-mobile-live]'),
      activeIndex: -1,
      manualScrollY: null
    };
    section._lementsSpectrum = state;
    state.cards.forEach((card, index) => {
      card.addEventListener('click', () => activateSpectrum(section, index, 'manual'));
      card.addEventListener('focus', () => activateSpectrum(section, index, 'manual'));
      card.addEventListener('pointerenter', () => activateSpectrum(section, index, 'manual'), { passive: true });
    });
    activateSpectrum(section, 0);
  };

  const updateSpectrum = (section) => {
    const state = section._lementsSpectrum;
    if (!state || !state.cards.length) return;
    const bounds = section.getBoundingClientRect();
    if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
    if (state.manualScrollY !== null && Math.abs(window.scrollY - state.manualScrollY) < 28) return;
    state.manualScrollY = null;

    if (window.innerWidth <= 580) {
      const marker = window.innerHeight * 0.62;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      state.cards.forEach((card, index) => {
        const cardBounds = card.getBoundingClientRect();
        const distance = Math.abs(cardBounds.top + cardBounds.height / 2 - marker);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      activateSpectrum(section, closestIndex, 'scroll');
      return;
    }

    if (window.innerWidth > 900) {
      const travel = Math.max(bounds.height - window.innerHeight, 1);
      const pass = clamp(-bounds.top / travel);
      activateSpectrum(section, Math.round(pass * (state.cards.length - 1)), 'scroll');
      return;
    }

    const gridBounds = (state.grid || section).getBoundingClientRect();
    const gridCenter = gridBounds.top + gridBounds.height / 2;
    const flowStart = window.innerHeight * 0.9;
    const flowEnd = window.innerHeight * 0.38;
    const pass = clamp((flowStart - gridCenter) / Math.max(flowStart - flowEnd, 1));
    activateSpectrum(section, Math.round(pass * (state.cards.length - 1)), 'scroll');
  };

  const initPointerGlow = (surface) => {
    if (surface.dataset.pointerGlowReady) return;
    surface.dataset.pointerGlowReady = 'true';
    if (!pointerEnabled) return;

    let frame = 0;
    let nextX = 0.5;
    let nextY = 0.34;
    const render = () => {
      const horizontal = (nextX - 0.5) * 14 * motionIntensity;
      const vertical = (nextY - 0.34) * 10 * motionIntensity;
      surface.style.setProperty('--pointer-x', (nextX * 100).toFixed(2) + '%');
      surface.style.setProperty('--pointer-y', (nextY * 100).toFixed(2) + '%');
      surface.style.setProperty('--parallax-x', horizontal.toFixed(2) + 'px');
      surface.style.setProperty('--parallax-y', vertical.toFixed(2) + 'px');
      surface.style.setProperty('--parallax-soft-x', (horizontal * -0.25).toFixed(2) + 'px');
      surface.style.setProperty('--parallax-soft-y', (vertical * -0.25).toFixed(2) + 'px');
      frame = 0;
    };
    const requestRender = () => {
      if (!frame) frame = window.requestAnimationFrame(render);
    };
    surface.addEventListener('pointermove', (event) => {
      const bounds = surface.getBoundingClientRect();
      nextX = clamp((event.clientX - bounds.left) / bounds.width);
      nextY = clamp((event.clientY - bounds.top) / bounds.height);
      requestRender();
    }, { passive: true });
    surface.addEventListener('pointerleave', () => {
      nextX = 0.5;
      nextY = 0.34;
      requestRender();
    }, { passive: true });
  };

  const setActiveStorySection = (section) => {
    if (!section || section === activeStorySection) return;
    activeStorySection = section;
    storySections.forEach((candidate) => candidate.classList.toggle('is-story-active', candidate === section));
    const index = storySections.indexOf(section) + 1;
    const label = section.dataset.storyLabel || section.dataset.storyScene || '';
    const accent = section.dataset.storyAccent || 'var(--ion)';
    body.dataset.activeStoryScene = section.dataset.storyScene || 'signal';
    body.style.setProperty('--story-accent', accent);
    if (storyRailIndex) storyRailIndex.textContent = String(index).padStart(2, '0');
    if (storyRailLabel) storyRailLabel.textContent = label;
  };

  const updatePageStory = () => {
    const viewportHeight = Math.max(window.innerHeight, 1);
    const mobileFlow = window.innerWidth <= 900;
    const pageRange = Math.max(document.documentElement.scrollHeight - viewportHeight, 1);
    const pageProgress = clamp(window.scrollY / pageRange);
    document.documentElement.style.setProperty('--page-progress', pageProgress.toFixed(4));
    document.documentElement.style.setProperty('--rail-position', (pageProgress * 100).toFixed(2) + '%');

    const marker = viewportHeight * 0.46;
    let active = storySections[0] || null;
    let activeDistance = Number.POSITIVE_INFINITY;
    storySections.forEach((section) => {
      const bounds = section.getBoundingClientRect();
      const pass = clamp((viewportHeight - bounds.top) / (viewportHeight + bounds.height));
      const focus = clamp(1 - Math.abs((bounds.top + bounds.height / 2) - viewportHeight / 2) / ((bounds.height + viewportHeight) / 2));
      const exit = clamp(-bounds.top / Math.max(bounds.height * 0.76, 1));
      const visualFocus = motionEnabled ? focus : 1;
      const visualExit = motionEnabled && !mobileFlow ? exit : 0;
      const shift = motionEnabled && !mobileFlow ? (pass - 0.5) * -18 * motionIntensity : 0;

      section.style.setProperty('--scene-pass', pass.toFixed(4));
      section.style.setProperty('--scene-focus', visualFocus.toFixed(4));
      section.style.setProperty('--scene-exit', exit.toFixed(4));
      section.style.setProperty('--scene-shift-soft', shift.toFixed(2) + 'px');
      section.style.setProperty('--scene-shift-faint', (shift * -0.42).toFixed(2) + 'px');
      section.style.setProperty('--scene-scale', (1.012 - visualFocus * 0.012).toFixed(4));
      section.style.setProperty('--scene-exit-shift', (-visualExit * 58 * motionIntensity).toFixed(2) + 'px');
      section.style.setProperty('--scene-exit-scale', (1 - visualExit * 0.045).toFixed(4));
      section.style.setProperty('--scene-exit-opacity', (1 - visualExit * 0.82).toFixed(4));
      section.style.setProperty('--scene-aura-opacity', (0.34 * (1 - visualExit * 0.9)).toFixed(4));

      const distance = bounds.top <= marker && bounds.bottom > marker
        ? 0
        : Math.min(Math.abs(bounds.top - marker), Math.abs(bounds.bottom - marker));
      if (distance < activeDistance) {
        activeDistance = distance;
        active = section;
      }
    });

    setActiveStorySection(active);
    narratives.forEach((section) => updateNarrative(section, viewportHeight));
    houseStories.forEach((section) => updateHouseStory(section, viewportHeight));
    spectrumConsoles.forEach(updateSpectrum);
    scrollFrame = 0;
  };

  const requestStoryUpdate = () => {
    if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updatePageStory);
  };

  const refreshExperience = () => {
    storySections = [...document.querySelectorAll('[data-story-scene]')];
    narratives = [...document.querySelectorAll('[data-scroll-narrative]')];
    houseStories = [...document.querySelectorAll('[data-house-story]')];
    spectrumConsoles = [...document.querySelectorAll('[data-scroll-spectrum]')];
    narratives.forEach(initNarrative);
    houseStories.forEach(initHouseStory);
    spectrumConsoles.forEach(initSpectrum);
    document.querySelectorAll('[data-pointer-glow]').forEach(initPointerGlow);
    activeStorySection = null;
    updatePageStory();
  };

  refreshExperience();
  body.classList.add('continuous-story-ready');
  window.addEventListener('scroll', requestStoryUpdate, { passive: true });
  window.addEventListener('resize', requestStoryUpdate, { passive: true });
  document.addEventListener('shopify:section:load', refreshExperience);
  document.addEventListener('shopify:section:unload', refreshExperience);
})();
